export interface PromoCard {
  id: string
  title: string
  description: string
  cta?: string
  href?: string
  routeTo?: { name: string; params?: Record<string, string> }
  isSurvey?: boolean
  surveyOptions?: Array<{ value: string; label: string }>
}

const DISMISS_STORAGE_KEY = 'rm_sidebar_promo_dismissals'
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000 // 24 hours

function getDismissals(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function isDismissed(cardId: string): boolean {
  const dismissals = getDismissals()
  const dismissedAt = dismissals[cardId]
  if (!dismissedAt) return false
  return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS
}

function saveDismissal(cardId: string) {
  const dismissals = getDismissals()
  dismissals[cardId] = Date.now()
  // Clean up expired entries
  const now = Date.now()
  for (const id in dismissals) {
    if (now - dismissals[id] >= DISMISS_COOLDOWN_MS) delete dismissals[id]
  }
  localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(dismissals))
}

export function useSidebarPromo() {
  const route = useRoute()
  const teamStore = useTeamStore()
  const { isDemo } = storeToRefs(teamStore)

  const activeCard = ref<PromoCard | null>(null)
  const surveyAnswer = ref<string>('')

  /**
   * Listen for RM flow Code node output.
   * The flow sets window.rm_sidebar_offer and dispatches 'rm:sidebar-offer'.
   */
  function onSidebarOffer() {
    const offer = window.rm_sidebar_offer
    if (!offer || isDemo.value || isDismissed(offer.id)) {
      activeCard.value = null
      return
    }

    activeCard.value = {
      id: offer.id,
      title: offer.title,
      description: offer.description,
      cta: offer.cta,
      href: offer.href,
      routeTo: offer.route ? { name: offer.route } : undefined,
      isSurvey: offer.isSurvey,
      surveyOptions: offer.surveyOptions,
    }
  }

  onMounted(() => {
    window.addEventListener('rm:sidebar-offer', onSidebarOffer)
    // Pick up any offer that was set before this component mounted
    if (window.rm_sidebar_offer) {
      onSidebarOffer()
    }
  })

  onUnmounted(() => {
    window.removeEventListener('rm:sidebar-offer', onSidebarOffer)
  })

  // Re-run RM flows on route change so the Code node picks a new offer.
  // Skip the first emission — the bridge's debounced reset handles the initial load.
  let isFirstRoute = true
  watch(() => route.fullPath, () => {
    if (isFirstRoute) {
      isFirstRoute = false
      return
    }
    window.RM = window.RM || []
    window.RM.push(['reset'])
  })

  function dismiss(cardId: string) {
    activeCard.value = null
    saveDismissal(cardId)
    recordAnalyticsEvent(AnalyticsEventTypes.PROMO_DISMISSED, {
      card_id: cardId,
    })
  }

  function submitSurvey(answer: string) {
    if (activeCard.value) saveDismissal(activeCard.value.id)
    recordAnalyticsEvent(AnalyticsEventTypes.FEEDBACK_PROVIDED, {
      type: 'attribution_survey',
      answer,
    })
    activeCard.value = null
  }

  return {
    activeCard,
    dismiss,
    submitSurvey,
    surveyAnswer,
  }
}
